interface StampProps {
  checked: boolean;
  disabled?: boolean;
  ariaLabel: string;
  onChange: (checked: boolean) => void;
  size?: 'md' | 'sm';
}

/**
 * The app's "stamp" checkbox (docs/DESIGN.md component recipes) — shared by the checklist
 * (`checklist/TaskControl`) and the boss matrix's clear checkbox (`bosses/BossCell`), since both
 * are the same "mark this done" gesture and deserve the identical tactile feel. A real
 * `<input type="checkbox">`, visually hidden but still focusable/announced, paired with a styled
 * sibling `<span class="stamp-visual">` — `index.css` plays the 180ms scale pop + fading ring on
 * `input:checked ~ .stamp-visual` (fill-only under `prefers-reduced-motion`).
 */
export function Stamp({ checked, disabled = false, ariaLabel, onChange, size = 'md' }: StampProps) {
  const box = size === 'sm' ? 'size-4' : 'size-5';
  return (
    <label className="relative flex min-h-tap min-w-tap shrink-0 cursor-pointer items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={ariaLabel}
        className={['peer absolute cursor-pointer opacity-0 disabled:cursor-not-allowed', box].join(' ')}
      />
      <span
        aria-hidden="true"
        className={[
          'stamp-visual relative flex items-center justify-center rounded-[6px] border-2 border-border-strong bg-bg-elevated-hover font-bold text-transparent peer-checked:border-accent peer-checked:bg-accent peer-checked:text-accent-contrast peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent-border peer-disabled:opacity-40',
          box,
          size === 'sm' ? 'text-[0.55rem]' : 'text-[0.7rem]',
        ].join(' ')}
      >
        ✓
      </span>
    </label>
  );
}
